import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import archivedReportsActions from 'actions/archive';

import { RecordsTable, TableWithHeader } from 'dyl-components';

import ArchivedReportsToolbar from './ArchivedReportsToolbar';
import ArchivedReportsHeader from './Header';
import { ArchivedReportsRow } from './Row';
import ArchivedReportsViewModal from './ArchivedReportsViewModal';
import { ArchivedRecordsContext } from 'shared/context/ArchivedRecordsProvider';

const DEFAULT_MODAL_STATE = {
    open: false,
    contact_id: null,
    account_id: null,
};

const ArchivedReportsTable = () => {
    const [params] = useSearchParams();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { archived, counts, isReading } = useSelector((state) => state.archive);

    const searchQuery = params.get('search') || '';

    const [search, setSearch] = useState(searchQuery);

    const [filters, setFilters] = useState({
        archived_by_user_id: params.get('archived_by_user_id')?.split(','),
    });

    useEffect(() => {
        const query = new URLSearchParams(params);
        if (search.trim()) {
            query.set('search', search);
        } else {
            query.delete('search');
        }
        const { archived_by_user_id } = filters;
        if (archived_by_user_id?.length > 0) {
            query.set('archived_by_user_id', archived_by_user_id.join(','));
        } else {
            query.delete('archived_by_user_id');
        }
        const query_string = query.toString();

        navigate(`/settings/archived-records${query_string ? `?${query_string}` : ''}`,);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const [isModalOpen, setIsModalOpen] = useState(DEFAULT_MODAL_STATE);
    const { setIsArchivedView } = useContext(ArchivedRecordsContext);

    const navigateWithParams = (updatedParams) => {
        const query = new URLSearchParams(params);

        Object.entries(updatedParams).forEach(([key, value]) => {
            if (
                value === undefined ||
                value === null ||
                value === ''
            ) {
                query.delete(key);
            } else {
                query.set(key, value);
            }
        });

        const queryString = query.toString();

        navigate(
            `/settings/archived-records${queryString ? `?${queryString}` : ''}`
        );
    };

    const onPageChange = (_, { activePage }) => {
        navigateWithParams({
            page: activePage,
        });
    };

    const onChangeSearch = (_, { value }) => {
        setSearch(value);
    };

    const cancelFunction = () => {
        setSearch('');

        navigateWithParams({
            search: null,
            page: 1,
        });
    };

    const onSearchSubmit = (value) => {
        navigateWithParams({
            search: value.trim(),
            page: 1,
        });
    };

    const onFilter = (_, { name, value }) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    useEffect(() => {
        const payload = {
            ...Object.fromEntries(params),
            limit: 25,
            start_time: params.get("start") || undefined,
            end_time: params.get("end") || undefined,
        }
        delete payload.start;
        delete payload.end;
        dispatch(
            archivedReportsActions.readArchivedReports({
                ...payload,
            })
        );
    }, [dispatch, params]);

    const hasFilters =
        params.get('search') ||
        params.get('type') ||
        params.get('destination_type');

    return (
        <div className='DylNumbers'>
            <TableWithHeader
                header={
                    <ArchivedReportsToolbar
                        search={search}
                        onChangeSearch={onChangeSearch}
                        onSearchSubmit={onSearchSubmit}
                        cancelFunction={cancelFunction}
                        isReading={isReading}
                    />
                }
                table={
                    <RecordsTable
                        tableBodyClassName='NumbersTable'
                        isTableLoading={isReading}
                        isSearchResultEmpty={!counts?.archived_count && hasFilters}
                        isTableEmpty={!counts?.archived_count && !hasFilters}
                        tableEmptyMessage='No Records Archived'
                        TableHeader={
                            <ArchivedReportsHeader
                                onFilter={onFilter}
                                count={counts?.archived_count}
                            />
                        }
                        TableRows={archived?.map((report) => (
                            <ArchivedReportsRow
                                key={report.contact_id}
                                report={report}
                                setIsModalOpen={setIsModalOpen}
                                totalRecordsCount={counts?.total_count}
                                cancelFunction={cancelFunction}
                            />
                        ))}
                        recordsCount={counts?.archived_count}
                        activePage={params.get('page')}
                        onPageChange={onPageChange}
                        recordsName='Number'
                        hasPagination={counts?.archived_count > 5}
                    />
                }
            />

            <ArchivedReportsViewModal
                open={isModalOpen.open}
                onClose={() => { setIsModalOpen(DEFAULT_MODAL_STATE); setIsArchivedView(false);}}
                contact_id={isModalOpen.contact_id}
                account_id={isModalOpen.account_id}
            />
        </div>
    );
};

export default ArchivedReportsTable;